import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IAMService, SortDirection, User, UserSortField } from '@vendasta/iam';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

// SelectedSalesperson is needed to distinguish between the user clicking cancel to close the modal and
// them unselecting the salesperson
export interface SelectedSalesperson {
  salesperson: User;
}

@Component({
  selector: 'sales-ui-salesperson-selector-dialog',
  templateUrl: './salesperson-selector-dialog.component.html',
  styleUrls: ['./salesperson-selector-dialog.component.scss'],
})
export class SalespersonSelectorDialogComponent implements OnInit {
  selectedSalesperson: User;
  salespersonOptions$$ = new BehaviorSubject<User[]>([]);
  cursor = '';
  pageSize = 25;
  hasMore = true;
  searchCtrl = new UntypedFormControl('');
  searchTerm$$ = new BehaviorSubject<string>('');
  loadMore$$ = new BehaviorSubject<null>(null);
  partnerId: string;
  loading: boolean;

  @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;

  constructor(
    private readonly userLookupService: IAMService,
    @Inject(MAT_DIALOG_DATA) public data: { partnerId: string; previouslySelectedUser: string },
    private dialogRef: MatDialogRef<SalespersonSelectorDialogComponent, SelectedSalesperson>,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.partnerId = data.partnerId;
  }

  ngOnInit(): void {
    if (this.data?.previouslySelectedUser) {
      this.userLookupService
        .getMultiUsers([{ userId: this.data.previouslySelectedUser }])
        .pipe(take(1))
        .subscribe((user) => {
          if (!!user && user.length == 1) {
            this.selectedSalesperson = user[0];
          }
        });
    }
    this.searchCtrl.valueChanges.pipe(map((searchTerm) => searchTerm ?? '')).subscribe((searchTerm) => {
      this.cursor = '';
      this.hasMore = true;
      this.searchTerm$$.next(searchTerm);
    });
    this.searchTerm$$
      .pipe(
        debounceTime(200),
        switchMap((searchTerm) => {
          this.loading = true;
          this.cdr.detectChanges();

          return this.userLookupService.listUsers(
            this.partnerId,
            null,
            this.cursor,
            this.pageSize,
            ['sales_person'],
            [searchTerm],
            [
              {
                direction: SortDirection.SORT_DIRECTION_ASCENDING,
                field: UserSortField.USER_SORT_FIELD_FIRST_NAME,
              },
              {
                direction: SortDirection.SORT_DIRECTION_ASCENDING,
                field: UserSortField.USER_SORT_FIELD_LAST_NAME,
              },
            ],
          );
        }),
      )
      .subscribe((resp) => this.updateSalespersonOptions([], resp.users, resp.nextCursor, resp.hasMore));
    this.loadMore$$
      .pipe(
        withLatestFrom(this.searchTerm$$),
        switchMap(([, searchTerm]) => {
          this.loading = true;
          this.cdr.detectChanges();
          return this.userLookupService.listUsers(
            this.partnerId,
            null,
            this.cursor,
            this.pageSize,
            ['sales_person'],
            [searchTerm],
            [
              {
                direction: SortDirection.SORT_DIRECTION_ASCENDING,
                field: UserSortField.USER_SORT_FIELD_FIRST_NAME,
              },
              {
                direction: SortDirection.SORT_DIRECTION_ASCENDING,
                field: UserSortField.USER_SORT_FIELD_LAST_NAME,
              },
            ],
          );
        }),
        withLatestFrom(this.salespersonOptions$$),
      )
      .subscribe(([resp, currentSalespersonOptions]) =>
        this.updateSalespersonOptions(currentSalespersonOptions, resp.users, resp.nextCursor, resp.hasMore),
      );

    this.salespersonOptions$$.next([]);
  }

  selectSalesperson(event: MatAutocompleteSelectedEvent): void {
    this.selectedSalesperson = event?.option?.value as User;
    this.chipInput.nativeElement.value = '';
    this.searchCtrl.setValue('');
  }

  unselectSalesperson(): void {
    this.selectedSalesperson = null;
  }

  loadMore(): void {
    if (!this.hasMore) {
      return;
    }

    this.loadMore$$.next(null);
  }

  updateSalespersonOptions(
    currentSalespersonOptions: User[],
    newSalespersonOptions: User[],
    nextCursor: string,
    hasMore: boolean,
  ): void {
    this.salespersonOptions$$.next(currentSalespersonOptions.concat(newSalespersonOptions));
    this.cursor = nextCursor;
    this.hasMore = hasMore;
    this.loading = false;
    this.cdr.detectChanges();
  }

  select() {
    this.dialogRef.close({ salesperson: this.selectedSalesperson });
  }
}
